<template>
  <div class="contracts-list">
    <!-- Barra de acciones en lote -->
    <div v-if="selectedIds.size > 0" class="contracts-list__batch-bar">
      <span class="contracts-list__batch-count">{{ selectedIds.size }} seleccionados</span>
      <button class="contracts-list__batch-action" @click="emitBatch('generate-statements')">Generar estados de cuenta para todos</button>
      <button class="contracts-list__batch-action" @click="emitBatch('export-selection')">Exportar selección</button>
      <button class="contracts-list__batch-action" @click="emitBatch('bulk-reminder')">Enviar recordatorio de pago masivo</button>
      <button class="contracts-list__batch-clear" @click="clearSelection">Deseleccionar todo</button>
    </div>

    <!-- Encabezado de columnas -->
    <div class="contracts-list__col-header">
        <div class="contracts-list__col contracts-list__col--status"></div>

      <button class="contracts-list__col contracts-list__col--tenant contracts-list__col--sortable" @click="toggleSort('tenant')">
        Inquilino y nave
        <span v-if="sortKey === 'tenant'" class="mdi" :class="sortDir === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down'"></span>
      </button>

      <button class="contracts-list__col contracts-list__col--rent contracts-list__col--sortable" @click="toggleSort('rent')">
        Renta mensual
        <span v-if="sortKey === 'rent'" class="mdi" :class="sortDir === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down'"></span>
      </button>

      <button class="contracts-list__col contracts-list__col--vigencia contracts-list__col--sortable" @click="toggleSort('vigencia')">
        Vigencia
        <span v-if="sortKey === 'vigencia'" class="mdi" :class="sortDir === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down'"></span>
      </button>

      <button class="contracts-list__col contracts-list__col--payment contracts-list__col--sortable" @click="toggleSort('payment')">
        Estatus de pago
        <span v-if="sortKey === 'payment'" class="mdi" :class="sortDir === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down'"></span>
      </button>

      <button class="contracts-list__col contracts-list__col--process contracts-list__col--sortable" @click="toggleSort('process')">
        Proceso activo
        <span v-if="sortKey === 'process'" class="mdi" :class="sortDir === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down'"></span>
      </button>

      <div class="contracts-list__col contracts-list__col--actions"></div>
    </div>

    <!-- Filas con scroll -->
    <div class="contracts-list__body">
      <div
        v-for="(row, index) in sortedContracts"
        :key="row.id"
        class="contracts-list__row"
        :class="{
          'contracts-list__row--even': index % 2 === 1,
          'contracts-list__row--selected': selectedIds.has(row.id),
        }"
        @click="onRowClick(row)"
      >
        <div class="contracts-list__col contracts-list__col--status" @click.stop="toggleSelect(row.id)">
          <span
            v-if="!selectedIds.size"
            class="contracts-list__dot"
            :class="`contracts-list__dot--${row.statusColor}`"
          ></span>
          <input v-else type="checkbox" :checked="selectedIds.has(row.id)" class="contracts-list__checkbox" @click.stop="toggleSelect(row.id)" />
        </div>

        <div
          class="contracts-list__col contracts-list__col--tenant"
          @mouseenter="hoveredTenantId = row.id"
          @mouseleave="hoveredTenantId = null"
        >
          <div class="contracts-list__tenant-name-row">
            <span class="contracts-list__tenant-name">{{ row.tenant.name }}</span>
            <span
              v-if="row.tenant.contractCount > 1"
              class="contracts-list__tenant-badge"
              @click.stop="$emit('filter-by-tenant', row.tenant.id)"
            >
              {{ row.tenant.contractCount }}
            </span>
          </div>
          <div class="contracts-list__tenant-sub">
            <span class="contracts-list__plot-tag">{{ row.plot.name }}</span>
            <span v-if="row.plot.areaM2" class="contracts-list__plot-area">{{ row.plot.areaM2 }} m²</span>
          </div>

          <div v-if="hoveredTenantId === row.id && (row.tenant.razonSocial || row.tenant.rfc)" class="contracts-list__tooltip">
            <div v-if="row.tenant.razonSocial">{{ row.tenant.razonSocial }}</div>
            <div v-if="row.tenant.rfc" class="contracts-list__tooltip-rfc">RFC: {{ row.tenant.rfc }}</div>
          </div>
        </div>

        <div class="contracts-list__col contracts-list__col--rent">
          <span class="contracts-list__rent-currency">{{ row.rent.currency }}</span>
          <span class="contracts-list__rent-amount">{{ formatNumber(row.rent.amount) }}</span>
          <span
            v-if="row.escalation"
            class="mdi mdi-trending-up contracts-list__rent-icon contracts-list__rent-icon--escalation"
            :title="`Escalación el ${formatDate(row.escalation.date)} · Nueva renta: ${formatCurrency(row.escalation.newRent)}`"
          ></span>
          <span
            v-if="row.marketComparison.belowMarket"
            class="mdi mdi-information-outline contracts-list__rent-icon contracts-list__rent-icon--market"
            :title="`Precio de mercado: ${formatCurrency(row.marketComparison.marketRatePerM2)}/m² · ${row.marketComparison.diffPercent?.toFixed(1)}% por debajo`"
          ></span>
        </div>

        <div class="contracts-list__col contracts-list__col--vigencia">
          <div class="contracts-list__vigencia-dates">
            <span class="contracts-list__vigencia-start">{{ formatDate(row.vigencia.startDate) }}</span>
            <span class="mdi mdi-arrow-right contracts-list__vigencia-arrow"></span>
            <span class="contracts-list__vigencia-end" :class="`contracts-list__vigencia-end--${row.vigencia.urgencyColor}`">
              {{ formatDate(row.vigencia.endDate) }}
            </span>
          </div>
          <div class="contracts-list__vigencia-progress">
            <div
              class="contracts-list__vigencia-progress-fill"
              :class="`contracts-list__vigencia-progress-fill--${row.vigencia.urgencyColor}`"
              :style="{ width: row.vigencia.progressPercent + '%' }"
            ></div>
          </div>
        </div>

        <div class="contracts-list__col contracts-list__col--payment">
          <span class="contracts-list__payment-pill" :class="`contracts-list__payment-pill--${row.paymentStatus.label}`">
            {{ paymentLabel(row.paymentStatus) }}
          </span>
          <div v-if="row.paymentStatus.label === 'parcial'" class="contracts-list__payment-sub">
            {{ row.paymentStatus.percentPaid }}% pagado
          </div>
        </div>

        <div class="contracts-list__col contracts-list__col--process">
          <span v-if="row.activeProcess" class="contracts-list__process-tag" :class="`contracts-list__process-tag--${row.activeProcess.toLowerCase()}`">
            {{ processLabel(row.activeProcess) }}
          </span>
          <span v-else class="contracts-list__process-dash">—</span>
        </div>

        <div class="contracts-list__col contracts-list__col--actions" @click.stop>
          <button class="contracts-list__menu-btn" @click="openActionMenu(row.id, $event)">
            <span class="mdi mdi-dots-vertical"></span>
          </button>
            <Teleport to="body">
            <div
                v-if="openActionMenuId === row.id"
                class="contracts-list__action-menu"
                :style="{ top: actionMenuPosition.top + 'px', left: actionMenuPosition.left + 'px' }"
            >
                <button
                v-for="action in getRowActions(row)"
                :key="action.label"
                class="contracts-list__action-item"
                :class="{ 'contracts-list__action-item--urgent': action.urgent }"
                @click="onRowAction(row, action)"
                >
                <span v-if="action.urgent" class="contracts-list__action-dot"></span>
                {{ action.label }}
                </button>
            </div>
            </Teleport>
        </div>
      </div>

      <div class="contracts-list__fade"></div>
    </div>

    <!-- Pie de tabla -->
    <div class="contracts-list__footer">
      <div class="contracts-list__footer-left">
        {{ footerText }}
        <button v-if="selectedIds.size > 0" class="contracts-list__footer-clear" @click="clearSelection">Deseleccionar todo</button>
      </div>
      <div class="contracts-list__footer-right">
        <span class="contracts-list__stat" tabindex="0">
          <span class="contracts-list__stat-label">Superficie</span>
          <span class="contracts-list__footer-total">{{ totalAreaM2 }} m²</span>
          <span class="contracts-list__stat-tip" role="tooltip">
            <strong>Superficie total</strong>
            Suma de los metros cuadrados de las naves de los contratos que se muestran en la lista.
          </span>
        </span>
        <span class="contracts-list__stat" tabindex="0">
          <span class="contracts-list__stat-label">Renta mensual</span>
          <span class="contracts-list__footer-total contracts-list__footer-total--navy">{{ formatCurrency(totalRent) }}</span>
          <span class="contracts-list__stat-tip" role="tooltip">
            <strong>Renta mensual total</strong>
            Suma de la renta mensual pactada de los contratos de la lista. Es lo que se debería cobrar en un mes si todos pagaran completo.
          </span>
        </span>
        <span class="contracts-list__stat" tabindex="0">
          <span class="contracts-list__stat-label">Cobrado</span>
          <span class="contracts-list__footer-total" :class="collectedHealthy ? 'contracts-list__footer-total--green' : 'contracts-list__footer-total--red'">
            {{ formatCurrency(totalCollected) }}
          </span>
          <span class="contracts-list__stat-tip" role="tooltip">
            <strong>Cobrado del periodo vigente</strong>
            Lo que ya se ha pagado del cargo más reciente ya vencido de cada contrato de la lista. Verde si llega al 90 % o más de la renta mensual total; rojo si es menos.
          </span>
        </span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'

function handleOutsideClick(e) {
  if (!e.target.closest('.contracts-list__menu-btn') && !e.target.closest('.contracts-list__action-menu')) {
    openActionMenuId.value = null
  }
}
onMounted(() => window.addEventListener('click', handleOutsideClick))
onUnmounted(() => window.removeEventListener('click', handleOutsideClick))

const props = defineProps({
  contracts: { type: Array, default: () => [] },
  filterLabel: { type: String, default: '' },
  searchQuery: { type: String, default: '' },
})

watch(
  () => props.contracts,
  (newContracts) => {
    console.log('ContractsListSection recibió:', newContracts.length, 'contratos')
    console.log('¿Incluye el de negociación?', newContracts.some((c) => c.status === 'PENDING'))
  },
  { immediate: true }
)

const emit = defineEmits(['select-contract', 'filter-by-tenant', 'row-action', 'batch-action'])


const actionMenuPosition = ref({ top: 0, left: 0 })

function openActionMenu(rowId, event) {
  if (openActionMenuId.value === rowId) {
    openActionMenuId.value = null
    return
  }
  const rect = event.currentTarget.getBoundingClientRect()
  actionMenuPosition.value = { top: rect.top - 4, left: rect.right - 200 }
  openActionMenuId.value = rowId
}



const sortKey = ref(null)
const sortDir = ref('asc')

function toggleSort(key) {
  if (sortKey.value !== key) {
    sortKey.value = key
    sortDir.value = 'asc'
  } else if (sortDir.value === 'asc') {
    sortDir.value = 'desc'
  } else {
    sortKey.value = null
    sortDir.value = 'asc'
  }
}

function sortValue(row, key) {
  if (key === 'tenant') return row.tenant.name.toLowerCase()
  if (key === 'rent') return row.rent.amount
  if (key === 'vigencia') return new Date(row.vigencia.endDate).getTime()
  if (key === 'payment') return row.paymentStatus.label
  if (key === 'process') return row.activeProcess || ''
  return ''
}

const sortedContracts = computed(() => {
  if (!sortKey.value) return props.contracts
  const list = [...props.contracts]
  list.sort((a, b) => {
    const va = sortValue(a, sortKey.value)
    const vb = sortValue(b, sortKey.value)
    if (va < vb) return sortDir.value === 'asc' ? -1 : 1
    if (va > vb) return sortDir.value === 'asc' ? 1 : -1
    return 0
  })
  return list
})

// --- Selección múltiple ---
const selectedIds = ref(new Set())

function toggleSelect(id) {
  const next = new Set(selectedIds.value)
  if (next.has(id)) next.delete(id)
  else next.add(id)
  selectedIds.value = next
}

function clearSelection() {
  selectedIds.value = new Set()
}

function emitBatch(action) {
  emit('batch-action', { action, ids: [...selectedIds.value] })
}

function onRowClick(row) {
  if (selectedIds.value.size > 0) {
    toggleSelect(row.id)
    return
  }
  emit('select-contract', row)
}

// --- Tooltip inquilino ---
const hoveredTenantId = ref(null)

// --- Menú de acciones ---
const openActionMenuId = ref(null)

function getRowActions(row) {
  if (row.paymentStatus.label === 'vencido') {
    return [
      { label: 'Registrar pago', action: 'register-payment', urgent: true },
      { label: 'Ver detalle completo', action: 'view-detail' },
      { label: 'Iniciar renovación', action: 'start-renewal' },
      { label: 'Generar estado de cuenta', action: 'generate-statement' },
      { label: 'Editar condiciones', action: 'edit-terms' },
      { label: 'Marcar incidencia', action: 'flag-incident' },
    ]
  }
  if (row.vigencia.daysRemaining < 0) {
    return [
      { label: 'Iniciar renovación', action: 'start-renewal', urgent: true },
      { label: 'Ver detalle completo', action: 'view-detail' },
      { label: 'Generar carta de terminación', action: 'generate-termination' },
      { label: 'Archivar contrato', action: 'archive' },
    ]
  }
  if (row.activeProcess === 'NEGOTIATION') {
    return [
      { label: 'Ver detalle completo', action: 'view-detail' },
      { label: 'Actualizar estatus de negociación', action: 'update-negotiation' },
      { label: 'Confirmar renovación', action: 'confirm-renewal' },
      { label: 'Cancelar proceso', action: 'cancel-process' },
    ]
  }
  return [
    { label: 'Registrar pago', action: 'register-payment' },
    { label: 'Ver detalle completo', action: 'view-detail' },
    { label: 'Iniciar renovación', action: 'start-renewal' },
    { label: 'Generar estado de cuenta', action: 'generate-statement' },
    { label: 'Editar condiciones', action: 'edit-terms' },
    { label: 'Marcar incidencia', action: 'flag-incident' },
  ]
}

function onRowAction(row, action) {
  openActionMenuId.value = null
  emit('row-action', { action: action.action, contract: row })
}

// --- Formato ---
function formatNumber(n) {
  return new Intl.NumberFormat('es-MX', { maximumFractionDigits: 0 }).format(n)
}

function formatCurrency(n) {
  if (n === null || n === undefined) return '—'
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(n)
}

function formatDate(iso) {
  return new Date(iso).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })
}

function paymentLabel(ps) {
  if (ps.label === 'al-corriente') return 'Al corriente'
  if (ps.label === 'parcial') return 'Parcial'
  if (ps.label === 'vencido') return `Vencido (${ps.daysOverdue}d)`
  return 'Sin registro'
}

function processLabel(p) {
  if (p === 'RENEWAL') return 'Renovación'
  if (p === 'NEGOTIATION') return 'Negociación'
  if (p === 'DISPUTE') return 'Disputa'
  return p
}

// --- Pie de tabla ---
const footerText = computed(() => {
  const total = sortedContracts.value.length
  if (selectedIds.value.size > 0) return `${selectedIds.value.size} de ${props.contracts.length} contratos seleccionados`
  if (props.searchQuery) return `${total} resultado${total === 1 ? '' : 's'} para "${props.searchQuery}"`
  if (props.filterLabel && props.filterLabel !== 'todos') return `${total} contratos ${props.filterLabel}`
  return `${total} contratos en total`
})

const totalAreaM2 = computed(() =>
  formatNumber(sortedContracts.value.reduce((sum, r) => sum + (r.plot.areaM2 || 0), 0))
)
const totalRent = computed(() => sortedContracts.value.reduce((sum, r) => sum + r.rent.amount, 0))
const totalCollected = computed(() => sortedContracts.value.reduce((sum, r) => sum + r.collectedAmount, 0))
const collectedHealthy = computed(() => (totalRent.value === 0 ? true : totalCollected.value / totalRent.value >= 0.9))
</script>

<style scoped>
@container contracts-list (max-width: 1000px) {
  .contracts-list .contracts-list__col-header,
  .contracts-list .contracts-list__row {
    column-gap: 0.6rem;
  }

  .contracts-list .contracts-list__col--sortable {
    font-size: 9px;
    padding: 0 0.35rem;
    gap: 0.15rem;
  }

  .contracts-list .contracts-list__tenant-name {
    font-size: 12.5px;
  }

  .contracts-list .contracts-list__tenant-name-row {
    gap: 0.25rem;
  }

  .contracts-list .contracts-list__tenant-badge {
    width: 14px;
    height: 14px;
    font-size: 9px;
    flex-shrink: 0;
  }
}

@container contracts-list (max-width: 700px) {
  .contracts-list .contracts-list__col-header,
  .contracts-list .contracts-list__row {
    grid-template-columns: 32px minmax(0, 34fr) minmax(0, 16fr) minmax(0, 22fr) minmax(0, 16fr) 8fr;
    column-gap: 0.6rem;
  }
  .contracts-list .contracts-list__col--process {
    display: none;
  }
  .contracts-list .contracts-list__vigencia-arrow {
    display: none;
  }
  .contracts-list .contracts-list__vigencia-start {
    display: none;
  }
}

@container contracts-list (max-width: 520px) {
  .contracts-list .contracts-list__col-header,
  .contracts-list .contracts-list__row {
    grid-template-columns: 32px minmax(0, 45fr) minmax(0, 20fr) minmax(0, 25fr) 8fr;
  }
  .contracts-list .contracts-list__col--payment {
    display: none;
  }
}
.contracts-list {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  background: var(--bg-surface-1);
  border: 1px solid var(--border-active);
  border-radius: 10px;
  overflow: hidden;
  margin: 0 1.5rem 1.5rem;
  container-type: inline-size;
  container-name: contracts-list;
}

.contracts-list__body::-webkit-scrollbar {
  width: 6px;
}

.contracts-list__body::-webkit-scrollbar-thumb {
  background: var(--border-active);
  border-radius: 3px;
}

.contracts-list__body::-webkit-scrollbar-track {
  background: transparent;
}

/* Barra de acciones en lote */
.contracts-list__batch-bar {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.5rem 1rem;
  background: #eff6ff;
  border-bottom: 1px solid #bfdbfe;
  flex-shrink: 0;
}

.contracts-list__batch-count {
  font-family: var(--font-body);
  font-size: 12px;
  font-weight: 500;
  color: var(--accent-blue);
}

.contracts-list__batch-action {
  background: none;
  border: none;
  color: var(--accent-blue);
  font-family: var(--font-body);
  font-size: 12px;
  cursor: pointer;
  padding: 0;
}

.contracts-list__batch-clear {
  margin-left: auto;
  background: none;
  border: none;
  color: var(--text-muted);
  font-family: var(--font-body);
  font-size: 12px;
  cursor: pointer;
  padding: 0;
}

/* Encabezado de columnas */
.contracts-list__col-header {
  display: grid;
  grid-template-columns: 32px minmax(0, 30fr) minmax(0, 16fr) minmax(0, 17fr) minmax(0, 16fr) minmax(0, 12fr) minmax(0, 8fr);
  column-gap: 1rem;
  height: 36px;
  border-bottom: 1px solid #e2e8f0;
  flex-shrink: 0;
  background: var(--bg-surface-1);
}

.contracts-list__col--sortable {
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-family: var(--font-body);
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #94a3b8;
  padding: 0 0.5rem;
}

.contracts-list__col--sortable .mdi {
  font-size: 10px;
  color: var(--accent-blue);
}

/* Filas / scroll */
.contracts-list__body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  position: relative;
  -webkit-mask-image: linear-gradient(to bottom, black calc(100% - 20px), transparent 100%);
  mask-image: linear-gradient(to bottom, black calc(100% - 20px), transparent 100%);
}

.contracts-list__fade {
  height: 1px;
}

.contracts-list__row {
  display: grid;
  grid-template-columns: 32px minmax(0, 30fr) minmax(0, 16fr) minmax(0, 17fr) minmax(0, 16fr) minmax(0, 12fr) minmax(0, 8fr);
  column-gap: 1rem;
  height: 52px;
  border-bottom: 1px solid #f1f5f9;
  background: #ffffff;
  cursor: pointer;
  transition: background 80ms ease;
}

.contracts-list__row--even {
  background: #fafafa;
}

.contracts-list__row:hover {
  background: #eff6ff;
}

.contracts-list__row--selected {
  background: #dbeafe;
}

.contracts-list__col {
  display: flex;
  align-items: center;
  padding: 0 0.5rem;
  min-width: 0;
  position: relative;
}

.contracts-list__col--status {
  justify-content: center;
  position: relative;
}

.contracts-list__filter-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: #94a3b8;
  display: flex;
}

.contracts-list__filter-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
  padding: 0.4rem;
  z-index: 50;
  min-width: 160px;
}

.contracts-list__filter-option {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-family: var(--font-body);
  font-size: 12px;
  color: var(--text-content);
  padding: 0.3rem 0.4rem;
  cursor: pointer;
}

.contracts-list__filter-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.contracts-list__dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  transition: width 80ms ease, height 80ms ease;
}

.contracts-list__row:hover .contracts-list__dot {
  width: 12px;
  height: 12px;
}

.contracts-list__checkbox {
  width: 14px;
  height: 14px;
  accent-color: var(--accent-blue);
}

.contracts-list__dot--green,
.contracts-list__filter-dot--green { background: #10b981; }
.contracts-list__dot--amber,
.contracts-list__filter-dot--amber { background: #f0a500; }
.contracts-list__dot--red,
.contracts-list__filter-dot--red { background: #e84545; }
.contracts-list__dot--blue,
.contracts-list__filter-dot--blue { background: #3b82f6; }
.contracts-list__dot--gray,
.contracts-list__filter-dot--gray { background: #94a3b8; }

/* Inquilino y nave */
.contracts-list__col--tenant {
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 0.15rem;
}

.contracts-list__tenant-name-row {
  display: flex;
  align-items: center;
  gap: 0.35rem;
}

.contracts-list__tenant-name {
  font-family: var(--font-body);
  font-size: 14px;
  font-weight: 500;
  color: #1e3a5f;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.contracts-list__tenant-badge {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: var(--accent-blue);
  color: #ffffff;
  font-size: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
}

.contracts-list__tenant-sub {
  display: flex;
  align-items: center;
  gap: 0.35rem;
}

.contracts-list__plot-tag {
  background: #f1f5f9;
  color: #64748b;
  font-size: 10px;
  padding: 0.1rem 0.35rem;
  border-radius: 4px;
}

.contracts-list__plot-area {
  font-size: 10px;
  color: #94a3b8;
}

.contracts-list__tooltip {
  position: absolute;
  top: 100%;
  left: 0.5rem;
  z-index: 60;
  background: #1e3a5f;
  color: #ffffff;
  font-size: 11px;
  padding: 0.4rem 0.6rem;
  border-radius: 6px;
  white-space: nowrap;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.contracts-list__tooltip-rfc {
  color: rgba(255, 255, 255, 0.7);
  margin-top: 0.15rem;
}

/* Renta */
.contracts-list__col--rent {
  gap: 0.25rem;
  justify-content: center;
}

.contracts-list__rent-currency {
  font-family: var(--font-body);
  font-size: 10px;
  color: #94a3b8;
}

.contracts-list__rent-amount {
  font-family: var(--font-mono);
  font-size: 13px;
  color: #1e3a5f;
}

.contracts-list__rent-icon {
  font-size: 12px;
  cursor: help;
}

.contracts-list__rent-icon--escalation { color: #10b981; }
.contracts-list__rent-icon--market { color: #f0a500; }

/* Vigencia */
.contracts-list__col--vigencia {
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  gap: 0.2rem;
}

.contracts-list__vigencia-dates {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  min-width: 0;
  justify-content: center;
}

.contracts-list__vigencia-start,
.contracts-list__vigencia-end {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.contracts-list__vigencia-start {
  font-family: var(--font-body);
  font-size: 11px;
  color: #94a3b8;
}

.contracts-list__vigencia-arrow {
  font-size: 10px;
  color: #cbd5e1;
}

.contracts-list__vigencia-end {
  font-family: var(--font-body);
  font-size: 12px;
  font-weight: 500;
}

.contracts-list__vigencia-end--navy { color: #1e3a5f; }
.contracts-list__vigencia-end--gray { color: #374151; }
.contracts-list__vigencia-end--amber { color: #f0a500; }
.contracts-list__vigencia-end--red { color: #e84545; }
.contracts-list__vigencia-end--green { color: #10b981; }

.contracts-list__vigencia-progress {
  height: 2px;
  background: #f1f5f9;
  border-radius: 1px;
  overflow: hidden;
}

.contracts-list__vigencia-progress-fill {
  height: 100%;
}

.contracts-list__vigencia-progress-fill--navy { background: #1e3a5f; }
.contracts-list__vigencia-progress-fill--gray { background: #374151; }
.contracts-list__vigencia-progress-fill--amber { background: #f0a500; }
.contracts-list__vigencia-progress-fill--red { background: #e84545; }
.contracts-list__vigencia-progress-fill--green { background: #10b981; }

/* Estatus de pago */
.contracts-list__col--payment {
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.15rem;
}

.contracts-list__payment-pill {
  border-radius: 20px;
  padding: 0.15rem 0.55rem;
  font-family: var(--font-body);
  font-size: 11px;
  font-weight: 500;
}

.contracts-list__payment-pill--al-corriente { background: #dcfce7; color: #16a34a; }
.contracts-list__payment-pill--parcial { background: #fef3c7; color: #d97706; }
.contracts-list__payment-pill--vencido { background: #fee2e2; color: #dc2626; }
.contracts-list__payment-pill--sin-registro { background: #f1f5f9; color: #64748b; }

.contracts-list__payment-sub {
  font-size: 10px;
  color: #94a3b8;
}

/* Proceso activo */
.contracts-list__col--process {
  overflow: hidden;
  justify-content: center;
}

.contracts-list__process-tag {
  border: 1px solid;
  border-radius: 4px;
  padding: 0.1rem 0.4rem;
  font-family: var(--font-body);
  font-size: 10px;
  background: transparent;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  display: inline-block;
}

.contracts-list__process-tag--renewal { color: #3b82f6; border-color: #3b82f6; }
.contracts-list__process-tag--negotiation { color: #10b981; border-color: #10b981; }
.contracts-list__process-tag--dispute { color: #e84545; border-color: #e84545; }

.contracts-list__process-dash {
  color: #cbd5e1;
  width: 100%;
  text-align: center;
}

/* Acciones */
.contracts-list__col--actions {
  justify-content: center;
  position: relative;
}

.contracts-list__menu-btn {
  background: none;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  display: flex;
}

.contracts-list__action-menu {
  position: fixed;
  transform: translateY(-100%);
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
  min-width: 200px;
  z-index: 1000;
  padding: 0.3rem;
}

.contracts-list__action-item {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  width: 100%;
  text-align: left;
  background: none;
  border: none;
  font-family: var(--font-body);
  font-size: 12px;
  color: var(--text-content);
  padding: 0.4rem 0.5rem;
  cursor: pointer;
  border-radius: 5px;
}

.contracts-list__action-item:hover {
  background: #f8fafc;
}

.contracts-list__action-item--urgent {
  font-weight: 500;
}

.contracts-list__action-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #e84545;
  flex-shrink: 0;
}

/* Pie de tabla */
.contracts-list__footer {
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 1rem;
  background: #f8f9fc;
  border-top: 1px solid #e2e8f0;
  flex-shrink: 0;
}

.contracts-list__footer-left {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-family: var(--font-body);
  font-size: 12px;
  color: #64748b;
}

.contracts-list__footer-clear {
  background: none;
  border: none;
  color: var(--accent-blue);
  font-size: 12px;
  cursor: pointer;
  padding: 0;
}

.contracts-list__footer-right {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.contracts-list__stat {
  position: relative;
  display: inline-flex;
  align-items: baseline;
  gap: 0.4rem;
  cursor: help;
  outline: none;
}

.contracts-list__stat-label {
  font-family: var(--font-body);
  font-size: 11px;
  color: #94a3b8;
}

.contracts-list__stat-tip {
  position: absolute;
  bottom: calc(100% + 8px);
  right: 0;
  width: 250px;
  background: #1e3a5f;
  color: #ffffff;
  border-radius: 8px;
  padding: 0.55rem 0.7rem;
  font-family: var(--font-body);
  font-size: 11px;
  line-height: 1.4;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.2);
  opacity: 0;
  visibility: hidden;
  transform: translateY(2px);
  transition: opacity 0.12s ease, transform 0.12s ease;
  pointer-events: none;
  z-index: 20;
}

.contracts-list__stat-tip strong {
  display: block;
  font-size: 11px;
  margin-bottom: 0.15rem;
}

.contracts-list__stat:hover .contracts-list__stat-tip,
.contracts-list__stat:focus .contracts-list__stat-tip {
  opacity: 1;
  visibility: visible;
  transform: none;
}

.contracts-list__footer-total {
  font-family: var(--font-mono);
  font-size: 12px;
  color: #64748b;
}

.contracts-list__footer-total--navy { color: #1e3a5f; }
.contracts-list__footer-total--green { color: #16a34a; }
.contracts-list__footer-total--red { color: #dc2626; }
</style>